import { useEffect, useState } from "react";
import { dashboardApi } from "../../api/dashboardApi";
import { useAuth } from "../../auth/AuthContext";
import { CarelTechnicalDetailPanel } from "./CarelTechnicalDetailPanel";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import "../../styles/ifi-galileo.css";

const BAR_GRADIENT_ID = "ifi-bar-gradient";
const PIE_COLORS = ["#6366f1", "#f59e0b", "#ef4444", "#10b981", "#8b5cf6"];

export function IfiGalileoDashboardWidgets() {
  const { token } = useAuth() as any;
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    const fetchOverview = () => {
      setError(null);
      dashboardApi
        .getVendorOverview(token, "IFI_GALILEO")
        .then((res: any) => {
          if (cancelled) return;
          setOverview(res);
          setLoading(false);
        })
        .catch((e) => {
          if (cancelled) return;
          setError(e?.message ?? "Errore caricamento dashboard IFI Galileo");
          setLoading(false);
        });
    };

    // primo fetch immediato
    setLoading(true);
    fetchOverview();

    // polling per avere KPI live (prestazioni, consumi, allarmi)
    const intervalId = window.setInterval(fetchOverview, 15000); // 15s

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [token]);

  if (loading) {
    return (
      <div className="ifi-loading">
        <div className="ifi-loading-spinner" />
        <p className="ifi-loading-text">Caricamento dashboard IFI Galileo…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ifi-error-card">
        <div className="ifi-error-icon">
          <i className="bi bi-exclamation-triangle-fill" />
        </div>
        <div>
          <div className="ifi-error-title">Dashboard IFI Galileo</div>
          <p className="ifi-error-message">{error}</p>
        </div>
      </div>
    );
  }

  const energyData = Array.isArray(overview?.energyLast7d) ? overview.energyLast7d : [];
  const alarmsData =
    Array.isArray(overview?.alarmsByMachine) && overview.alarmsByMachine.length > 0
      ? overview.alarmsByMachine
      : Array.isArray(overview?.alarmsByCode)
        ? overview.alarmsByCode
        : [];

  return (
    <div className="ifi-galileo-page">
      {/* Hero */}
      <div className="ifi-hero">
        <div className="ifi-hero-content">
          <div className="ifi-hero-brand">
            <div className="ifi-hero-icon-wrap ifi-icon-rotate">
              <i className="bi bi-gear-wide-connected" />
            </div>
            <div>
              <h1 className="ifi-hero-title">IFI Galileo</h1>
              <p className="ifi-hero-subtitle">Monitoraggio macchine e consumi in tempo reale</p>
            </div>
          </div>
          <span className="ifi-hero-badge">
            <span className="ifi-hero-badge-dot" />
            Live
          </span>
        </div>
      </div>

      {/* KPI */}
      <div className="ifi-kpis">
        <div className="ifi-kpi-card" data-accent="perf">
          <div className="ifi-kpi-icon">
            <i className="bi bi-speedometer2" />
          </div>
          <div className="ifi-kpi-label">Prestazioni</div>
          <div className="ifi-kpi-value">
            {overview?.performance != null ? `${Number(overview.performance).toFixed(1)}%` : "—"}
          </div>
        </div>
        <div className="ifi-kpi-card" data-accent="energy">
          <div className="ifi-kpi-icon">
            <i className="bi bi-lightning-charge-fill" />
          </div>
          <div className="ifi-kpi-label">Consumo oggi</div>
          <div className="ifi-kpi-value">
            {overview?.totalEnergy != null ? `${Number(overview.totalEnergy).toFixed(1)} kWh` : "—"}
          </div>
        </div>
        <div className="ifi-kpi-card" data-accent="alarms">
          <div className="ifi-kpi-icon">
            <i className="bi bi-exclamation-triangle-fill" />
          </div>
          <div className="ifi-kpi-label">Allarmi attivi</div>
          <div className="ifi-kpi-value">{overview?.activeAlarms ?? 0}</div>
        </div>
        <div className="ifi-kpi-card" data-accent="online">
          <div className="ifi-kpi-icon">
            <i className="bi bi-check-circle-fill" />
          </div>
          <div className="ifi-kpi-label">Macchine online</div>
          <div className="ifi-kpi-value">
            {overview?.onlineMachines ?? overview?.onlineCount ?? 0} / {overview?.totalMachines ?? overview?.machinesCount ?? 0}
          </div>
        </div>
        <div className="ifi-kpi-card" data-accent="perf">
          <div className="ifi-kpi-icon">
            <i className="bi bi-geo-alt-fill" />
          </div>
          <div className="ifi-kpi-label">In Via Zanardelli</div>
          <div className="ifi-kpi-value">
            {overview?.zanardelliCount ?? 0}
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="row g-4 mb-4">
        <div className="col-12 col-lg-7">
          <div className="ifi-card">
            <div className="ifi-card-header">
              <i className="bi bi-bar-chart-fill" />
              Consumi ultimi 7 giorni
            </div>
            <div className="ifi-chart-container">
              {energyData.length > 0 ? (
                <>
                  <defs>
                    <linearGradient id={BAR_GRADIENT_ID} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#6366f1" />
                      <stop offset="100%" stopColor="#818cf8" />
                    </linearGradient>
                  </defs>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={energyData} margin={{ top: 12, right: 12, left: 0, bottom: 8 }}>
                      <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip
                        formatter={(value: number) => [`${Number(value).toFixed(2)} kWh`, "Consumo"]}
                        contentStyle={{ borderRadius: 10, border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                      />
                      <Legend />
                      <Bar
                        dataKey="kwh"
                        fill={`url(#${BAR_GRADIENT_ID})`}
                        radius={[8, 8, 0, 0]}
                        name="Consumo"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </>
              ) : (
                <div className="ifi-chart-empty">
                  <i className="bi bi-graph-up" />
                  <p>Nessun dato consumo disponibile</p>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-5">
          <div className="ifi-card">
            <div className="ifi-card-header">
              <i className="bi bi-pie-chart-fill" />
              Distribuzione allarmi
            </div>
            <div className="ifi-chart-container">
              {alarmsData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={alarmsData.slice(0, 5)}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {alarmsData.slice(0, 5).map((_: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} stroke="#fff" strokeWidth={2} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value: number, name: string) => [value, name]}
                      contentStyle={{ borderRadius: 10, border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="ifi-chart-empty">
                  <i className="bi bi-emoji-smile" />
                  <p>Nessun allarme attivo</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4 mb-4">
        <div className="col-12">
          <CarelTechnicalDetailPanel />
        </div>
      </div>
    </div>
  );
}
